import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Grid,
  TextField,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { purchaseReturnService } from '../../utils/apiService';

const PurchaseReturnDetails = ({
  purchaseReturn,
  selectedInvoice,
  controlStyle,
  onPurchaseReturnChange,
  onInvoiceSelect,
  autoCodeEnabled = true,
}) => {
  const [isFetchingNumber, setIsFetchingNumber] = useState(false);

  // Auto-fetch the return number when autoCodeEnabled is true and no number has been set yet.
  // The parent (PurchaseReturnForm) also fetches it, but this acts as a reliable self-contained
  // fallback in case the parent fetch races or completes before this component mounts.
  useEffect(() => {
    if (!autoCodeEnabled) return;                    // manual mode — nothing to do
    if (purchaseReturn.returnNumber) return;         // already populated by parent

    let cancelled = false;
    const fetchNumber = async () => {
      setIsFetchingNumber(true);
      try {
        const res = await purchaseReturnService.getNextPurchaseReturnNumber();
        const raw =
          res?.data?.nextInvoiceNumber ||
          res?.nextInvoiceNumber ||
          (typeof res?.data === 'string' ? res.data : '') ||
          '';
        const resolved = typeof raw === 'object' ? (raw.nextInvoiceNumber || '') : raw;
        if (resolved && !cancelled) {
          onPurchaseReturnChange(prev => ({ ...prev, returnNumber: resolved }));
        }
      } catch (err) {
        console.error('PurchaseReturnDetails: error fetching next return number', err);
      } finally {
        if (!cancelled) setIsFetchingNumber(false);
      }
    };

    fetchNumber();
    return () => { cancelled = true; };
  }, [autoCodeEnabled]); // re-run only if the autoCode setting changes

  // Sync originalInvoice when selectedInvoice changes
  useEffect(() => {
    if (selectedInvoice && (selectedInvoice.invoiceNo || selectedInvoice.purchaseNumber)) {
      const invoiceNo = selectedInvoice.invoiceNo || selectedInvoice.purchaseNumber;
      onPurchaseReturnChange(prev => ({ ...prev, originalInvoice: invoiceNo }));
    }
  }, [selectedInvoice]);

  const handleReasonChange = (e) => {
    onPurchaseReturnChange(prev => ({ ...prev, reasonForReturn: e.target.value }));
  };

  const handleReturnNumberChange = (e) => {
    onPurchaseReturnChange(prev => ({ ...prev, returnNumber: e.target.value }));
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 0.5,
        mb: 0.5,
        borderRadius: '4px',
        boxShadow: 'none',
        border: 'none',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
        },
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5, fontSize: '0.875rem' }}>
        Purchase Return Details
      </Typography>
      <Grid container spacing={0.5}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Ref Purchase Number"
            value={selectedInvoice ? selectedInvoice.purchaseNumber || selectedInvoice.invoiceNo || selectedInvoice.number || '' : ''}
            InputProps={{
              readOnly: true,
            }}
            required
            fullWidth
            margin="dense"
            size="small"
            sx={controlStyle}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Return Number"
            value={isFetchingNumber ? '' : (purchaseReturn.returnNumber || '')}
            fullWidth
            margin="dense"
            size="small"
            placeholder={isFetchingNumber ? 'Generating…' : (autoCodeEnabled ? 'Auto-generated' : 'Enter return number')}
            InputProps={{
              readOnly: autoCodeEnabled,
              ...(isFetchingNumber && {
                endAdornment: (
                  <InputAdornment position="end">
                    <CircularProgress size={14} />
                  </InputAdornment>
                ),
              }),
            }}
            onChange={autoCodeEnabled ? undefined : handleReturnNumberChange}
            sx={{
              ...controlStyle,
              '& .MuiOutlinedInput-root': {
                borderRadius: 0,
                ...(autoCodeEnabled && { cursor: 'not-allowed' }),
              },
              ...(autoCodeEnabled && {
                '& .MuiOutlinedInput-input': { cursor: 'not-allowed' },
              }),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Ref Purchase Date"
              value={selectedInvoice ?
                (selectedInvoice.issueDate ? new Date(selectedInvoice.issueDate) :
                  selectedInvoice.date ? new Date(selectedInvoice.date) : null) :
                null
              }
              readOnly
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: "dense",
                  size: "small",
                  sx: controlStyle,
                  InputProps: { readOnly: true }
                }
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Date"
              value={purchaseReturn.date}
              readOnly
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: "dense",
                  size: "small",
                  sx: controlStyle,
                  InputProps: { readOnly: true }
                }
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Reason for Return"
            value={purchaseReturn.reasonForReturn || ''}
            onChange={handleReasonChange}
            fullWidth
            multiline
            rows={2.5}
            margin="dense"
            size="small"
            required
            error={!purchaseReturn.reasonForReturn}
            helperText={!purchaseReturn.reasonForReturn ? 'Reason is required' : ''}
            sx={{
              ...controlStyle,
              '& .MuiOutlinedInput-root': {
                minHeight: 90,
                borderRadius: 0
              }
            }}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default PurchaseReturnDetails;
